import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsAPI, cartAPI, recommendationsAPI, wishlistAPI, reviewsAPI, authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Star, ShoppingCart, Heart, ChevronLeft, ChevronRight, ZoomIn, X, User } from 'lucide-react';
import Layout from '@/components/Layout';
import { toast } from 'sonner';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [user, setUser] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  useEffect(() => {
    loadProduct();
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const checkAuth = async () => {
    try {
      const userData = await authAPI.getMe();
      setIsAuthenticated(true);
      setUser(userData);
      loadWishlistStatus();
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const loadWishlistStatus = async () => {
    try {
      const wishlist = await wishlistAPI.getWishlist();
      const isInWishlist = wishlist.some(item => item.product_id === productId);
      setIsWishlisted(isInWishlist);
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  const loadProduct = async () => {
    try {
      setLoading(true);
      const [productData, recsData, reviewsData] = await Promise.all([
        productsAPI.getById(productId),
        recommendationsAPI.getForProduct(productId).catch(() => []),
        reviewsAPI.getReviews(productId).catch(() => [])
      ]);
      setProduct(productData);
      setRecommendations(recsData);
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }
    
    try {
      await cartAPI.addToCart(productId, quantity);
      toast.success(`Added ${quantity} item(s) to cart`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to wishlist');
      navigate('/login');
      return;
    }

    setWishlistLoading(true);
    try {
      if (isWishlisted) {
        await wishlistAPI.removeFromWishlist(productId);
        setIsWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await wishlistAPI.addToWishlist(productId);
        setIsWishlisted(true);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    } finally {
      setWishlistLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to submit a review');
      navigate('/login');
      return;
    }

    if (!reviewForm.comment.trim()) {
      toast.error('Please write a review comment');
      return;
    }

    setSubmittingReview(true);
    try {
      const newReview = await reviewsAPI.createReview({
        product_id: productId,
        rating: reviewForm.rating,
        comment: reviewForm.comment
      });
      setReviews([newReview, ...reviews]);
      setReviewForm({ rating: 5, comment: '' });
      setShowReviewForm(false);
      toast.success('Review submitted successfully!');
      loadProduct();
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const nextImage = () => {
    if (product?.images?.length > 1) {
      setSelectedImage((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product?.images?.length > 1) {
      setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="skeleton h-96 rounded-2xl"></div>
              <div className="space-y-4">
                <div className="skeleton h-12 w-3/4"></div>
                <div className="skeleton h-6 w-1/2"></div>
                <div className="skeleton h-24 w-full"></div>
                <div className="skeleton h-12 w-1/3"></div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) return null;

  const avgRating = product.rating || 4.5;
  const reviewCount = product.review_count || reviews.length;

  return (
    <Layout>
      <div className="section-padding" data-testid="product-detail-page">
        <div className="container-custom">
          {/* Product Detail */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="relative aspect-square overflow-hidden rounded-2xl border border-border group">
                <img
                  src={product.images?.[selectedImage] || product.images?.[0] || 'https://images.unsplash.com/photo-1518117823675-9a4bb23df61c'}
                  alt={product.name}
                  className="w-full h-full object-cover cursor-zoom-in"
                  onClick={() => setShowImageModal(true)}
                  data-testid="product-main-image"
                />
                {product.images?.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft size={24} className="text-gray-800" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight size={24} className="text-gray-800" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowImageModal(true)}
                  className="absolute bottom-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white shadow-lg"
                >
                  <ZoomIn size={20} className="text-gray-800" />
                </button>
              </div>
              
              {/* Thumbnail Gallery */}
              {product.images?.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`aspect-square overflow-hidden rounded-lg border-2 transition-all ${selectedImage === idx ? 'border-accent ring-2 ring-accent/30' : 'border-border hover:border-accent/50'}`}
                      data-testid={`product-thumbnail-${idx}`}
                    >
                      <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-6">
              <div>
                <p className="text-sm text-accent font-medium mb-2">{product.brand}</p>
                <h1 className="text-4xl font-bold mb-4" data-testid="product-name">{product.name}</h1>
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        size={18} 
                        className={star <= Math.round(avgRating) ? 'fill-accent text-accent' : 'text-gray-300'} 
                      />
                    ))}
                  </div>
                  <span className="text-muted-foreground">({reviewCount} reviews)</span>
                </div>
                <p className="text-3xl font-bold font-mono mb-6" data-testid="product-price">
                  KES {product.price?.toLocaleString()}
                </p>
              </div>

              <div>
                <p className="text-lg text-muted-foreground" data-testid="product-description">{product.description}</p>
              </div>

              {/* Specifications */}
              {product.specs && Object.keys(product.specs).length > 0 && (
                <div className="bg-secondary/30 p-6 rounded-xl" data-testid="product-specs">
                  <h3 className="font-semibold text-lg mb-4">Specifications</h3>
                  <dl className="space-y-2">
                    {Object.entries(product.specs).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <dt className="text-muted-foreground">{key}:</dt>
                        <dd className="font-medium">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {/* Stock Status */}
              <div className="flex items-center gap-2">
                <span className={`inline-block w-3 h-3 rounded-full ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span className="text-sm">{product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}</span>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="flex gap-4">
                <div className="flex items-center border border-border rounded-full overflow-hidden">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-2 hover:bg-secondary"
                    data-testid="quantity-decrease"
                  >
                    -
                  </button>
                  <span className="px-6 py-2 font-medium" data-testid="quantity-display">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock || 10, quantity + 1))}
                    className="px-4 py-2 hover:bg-secondary"
                    data-testid="quantity-increase"
                  >
                    +
                  </button>
                </div>
                <Button
                  onClick={handleAddToCart}
                  className="btn-primary flex-1"
                  size="lg"
                  disabled={product.stock === 0}
                  data-testid="add-to-cart-button"
                >
                  <ShoppingCart className="mr-2" size={20} />
                  {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className={`rounded-full ${isWishlisted ? 'bg-red-50 border-red-300' : ''}`}
                  onClick={handleWishlistToggle}
                  disabled={wishlistLoading}
                  data-testid="wishlist-button"
                >
                  <Heart size={20} className={isWishlisted ? 'fill-red-500 text-red-500' : ''} />
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
                <div className="text-center">
                  <div className="text-2xl mb-1">✓</div>
                  <p className="text-sm font-medium">Genuine</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1">🛡️</div>
                  <p className="text-sm font-medium">Warranty</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1">📦</div>
                  <p className="text-sm font-medium">Fast Delivery</p>
                </div>
              </div>
            </div>
          </div>

          {/* Reviews Section */}
          <div className="mb-16" data-testid="reviews-section">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold">Customer Reviews</h2>
              {isAuthenticated && (
                <Button onClick={() => setShowReviewForm(!showReviewForm)} variant="outline">
                  {showReviewForm ? 'Cancel' : 'Write a Review'}
                </Button>
              )}
            </div>

            {/* Review Form */}
            {showReviewForm && (
              <Card className="mb-8">
                <CardContent className="p-6">
                  <form onSubmit={handleSubmitReview}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2">Your Rating</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                            className="p-1"
                          >
                            <Star
                              size={28}
                              className={star <= reviewForm.rating ? 'fill-accent text-accent' : 'text-gray-300'}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2">Your Review</label>
                      <Textarea
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        placeholder="Share your experience with this product..."
                        rows={4}
                        className="w-full"
                      />
                    </div>
                    <Button type="submit" disabled={submittingReview} className="btn-primary">
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Reviews List */}
            {reviews.length === 0 ? (
              <div className="text-center py-12 bg-secondary/20 rounded-2xl">
                <p className="text-muted-foreground">No reviews yet. Be the first to review this product!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <Card key={review.review_id} className="luxury-card">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                          <User size={24} className="text-accent" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold">{review.user_name}</h4>
                            <span className="text-sm text-muted-foreground">
                              {new Date(review.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mb-3">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                size={16}
                                className={star <= review.rating ? 'fill-accent text-accent' : 'text-gray-300'}
                              />
                            ))}
                          </div>
                          <p className="text-muted-foreground">{review.comment}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div data-testid="recommendations-section">
              <h2 className="text-3xl font-bold mb-8">You might also like</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6 lg:gap-8">
                {recommendations.map((rec) => (
                  <Card
                    key={rec.product_id}
                    className="card-product cursor-pointer"
                    onClick={() => navigate(`/product/${rec.product_id}`)}
                    data-testid={`recommendation-${rec.product_id}`}
                  >
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={rec.images?.[0] || 'https://images.unsplash.com/photo-1518117823675-9a4bb23df61c'}
                        alt={rec.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-3 sm:p-6">
                      <h3 className="font-semibold text-sm sm:text-lg mb-1 sm:mb-2 line-clamp-2">{rec.name}</h3>
                      <p className="text-base sm:text-2xl font-bold font-mono">KES {rec.price?.toLocaleString()}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20"
            onClick={() => setShowImageModal(false)}
          >
            <X size={24} className="text-white" />
          </button>
          <img
            src={product.images?.[selectedImage] || product.images?.[0]}
            alt={product.name}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {product.images?.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20"
              >
                <ChevronLeft size={32} className="text-white" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20"
              >
                <ChevronRight size={32} className="text-white" />
              </button>
            </>
          )}
        </div>
      )}
    </Layout>
  );
};

export default ProductDetailPage;